import { Model } from '@/clients/http'

import { CustomerHttpParams, CustomerResponses } from '@/types'

export class CustomerModel extends Model {
  // Define correlation keys for Redux state management
  public static readonly correlations = {
    GET_LIST: 'GET_CUSTOMER_LIST',
    GET_BY_ID: 'GET_CUSTOMER_BY_ID',
    CREATE: 'CREATE_CUSTOMER',
    UPDATE: 'UPDATE_CUSTOMER',
    DELETE: 'DELETE_CUSTOMER',
  }

  // Define endpoint builders
  public static readonly endpoints = {
    getList: (props?: CustomerHttpParams.GetList) =>
      this.createEndpoint({
        url: '/api/backoffice/customer',
        params: props?.params,
        query: props?.query,
      }),

    getById: (props?: CustomerHttpParams.GetById) =>
      this.createEndpoint({
        url: '/api/backoffice/customer/:id',
        params: props?.params,
        query: props?.query,
      }),

    create: (props?: CustomerHttpParams.Create) =>
      this.createEndpoint({
        url: '/api/backoffice/customer',
        params: props?.params,
        query: props?.query,
      }),

    update: (props?: CustomerHttpParams.Update) =>
      this.createEndpoint({
        url: '/api/backoffice/customer/:id',
        params: props?.params,
        query: props?.query,
      }),

    delete: (props?: CustomerHttpParams.Delete) =>
      this.createEndpoint({
        url: '/api/backoffice/customer/:id',
        params: props?.params,
        query: props?.query,
      }),
  }

  /**
   * Get paginated list of customers
   * Backend returns: { success: true, message: "Done", data: { page, length, count, result: [...] } }
   * After unwrapping: response.data = { page, length, count, result: [...] }
   */
  public static readonly getList = async (props?: CustomerHttpParams.GetList) => {
    return this.createHttpRequest<CustomerResponses.GetList, CustomerResponses.GetListError>({
      correlationKey: this.correlations.GET_LIST,
      payload: {
        method: 'get',
        url: this.endpoints.getList(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Get a single customer by ID
   * Backend returns: { success: true, message: "Done", data: { result: {...} } }
   * After unwrapping: response.data = { result: {...} }
   */
  public static readonly getById = async (props: CustomerHttpParams.GetById) => {
    return this.createHttpRequest<CustomerResponses.GetById, CustomerResponses.GetByIdError>({
      correlationKey: this.correlations.GET_BY_ID,
      payload: {
        method: 'get',
        url: this.endpoints.getById(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Create a new customer
   */
  public static readonly create = async (props: CustomerHttpParams.Create) => {
    return this.createHttpRequest<CustomerResponses.Create, CustomerResponses.CreateError>({
      correlationKey: this.correlations.CREATE,
      payload: {
        method: 'post',
        url: this.endpoints.create(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Update an existing customer
   */
  public static readonly update = async (props: CustomerHttpParams.Update) => {
    return this.createHttpRequest<CustomerResponses.Update, CustomerResponses.UpdateError>({
      correlationKey: this.correlations.UPDATE,
      payload: {
        method: 'put',
        url: this.endpoints.update(props),
        body: props.body,
        useBearerToken: true,
      },
      store: props?.store,
    })
  }

  /**
   * Delete a customer
   */
  public static readonly delete = async (props: CustomerHttpParams.Delete) => {
    return this.createHttpRequest<CustomerResponses.Delete, CustomerResponses.DeleteError>({
      correlationKey: this.correlations.DELETE,
      payload: {
        method: 'delete',
        url: this.endpoints.delete(props),
        useBearerToken: true,
      },
      store: props?.store,
    })
  }
}
